import { useTourFiltersStore } from '@/entities/filter/store/tour';
import { useComponentI18n } from '@/shared/composables/useComponentI18n.ts';
import { getFilterTitle } from '@/features/filter/dates/lib/title.ts';
import type ru from '@/features/filter/duration/i18n/ru.json';
import { ACCOMMODATION_GROUPS } from '@/shared/types/basic.ts';

type TSchema = typeof ru;

const isGroupSelected = (groupAtoms: readonly string[], selected: Set<string>) =>
  groupAtoms.some((a) => selected.has(a));

export function useTourAdditionalFilterOptions(messages: TSchema) {
  const filtersStore = useTourFiltersStore();

  const { t } = useComponentI18n({ ru: messages });

  const isActiveDatesFilter = computed(() => filtersStore.searchState.date.from || filtersStore.searchState.date.to);

  const isActivePersonsFilter = computed(() => filtersStore.searchState.persons > 1);

  const personsTitle = computed(
    () =>
      (isActivePersonsFilter.value && t('output.personsCount', { personsCount: filtersStore.searchState.persons })) ||
      undefined,
  );

  const isActiveComfortFilter = computed(() => filtersStore.searchState.comfort_type?.length);
  const isActiveActivityLevelFilter = computed(() => filtersStore.searchState.activity_level_category?.length);
  const isActiveAccommodationFilter = computed(() => filtersStore.searchState.accommodation_type?.length);
  const isActiveChildrenFilter = computed(() => filtersStore.searchState.childrenAllowed);

  const childrenTitle = computed(() => t('output.children'));
  const dateTitle = computed(() =>
    getFilterTitle(filtersStore.searchState.date.from, filtersStore.searchState.date.to),
  );

  const comfortTitle = computed(() => {
    return filtersStore.searchState.comfort_type.reduce((acc, item, index) => {
      const title = t(`output.${item}`);

      return index ? acc + `, ${title.toLowerCase()}` : title;
    }, '');
  });

  const activityLevelTitle = computed(() => {
    return filtersStore.searchState.activity_level_category.reduce((acc, item, index) => {
      const title = t(`output.${item}`);

      return index ? acc + `, ${title.toLowerCase()}` : title;
    }, '');
  });

  const accommodationTitle = computed(() => {
    const selected = new Set(filtersStore.searchState.accommodation_type ?? []);
    if (selected.size === 0) {
      return '';
    }

    const groupKeys = ACCOMMODATION_GROUPS.filter((g) => isGroupSelected(g.atoms, selected)).map((g) => g.key);

    return groupKeys.reduce((acc, key, index) => {
      const title = t(`output.${key}`);

      return index ? acc + `, ${title.toLowerCase()}` : title;
    }, '');
  });

  const accommodationGroupsCount = computed(() => {
    const selected = new Set(filtersStore.searchState.accommodation_type ?? []);
    if (selected.size === 0) {
      return 0;
    }

    return ACCOMMODATION_GROUPS.filter((g) => isGroupSelected(g.atoms, selected)).length;
  });

  return {
    isActiveDatesFilter,
    isActiveComfortFilter,
    isActiveActivityLevelFilter,
    isActiveAccommodationFilter,
    isActiveChildrenFilter,
    childrenTitle,
    dateTitle,
    isActivePersonsFilter,
    comfortTitle,
    activityLevelTitle,
    accommodationTitle,
    accommodationGroupsCount,
    personsTitle,
  };
}
